import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { api, ApiError, postForm, requestBlob } from '@/lib/api'
import { useAuth } from '@/lib/auth'
import { BASE } from '@/lib/base'
import { date, dateTime, money, percent, relative } from '@/lib/format'
import {
  canCancel,
  canDeleteDraft,
  canReEdit,
  canRecordLoss,
  canRecordOutcome,
  canSend,
  canSubmit,
  canWithdraw,
  presentation,
} from '@/lib/quotationStatus'
import { asRichHtml, htmlToPlainText, isRichTextEmpty, plainTextToHtml } from '@/lib/richText'
import { useAsync } from '@/lib/useAsync'
import { CustomerPicker } from '@/components/CustomerPicker'
import { QuotationPayments } from '@/components/QuotationPayments'
import { PageHeader, Tabs } from '@/components/ui/page'
import { Badge, Button, Card, CardHeader, cx, EmptyState, ErrorState, Loading, Spinner } from '@/components/ui/primitives'
import { ActionMenu, type ActionMenuItem } from '@/components/ui/actionmenu'
import type { ComboboxOption } from '@/components/ui/combobox'
import { Dialog } from '@/components/ui/dialog'
import { Checkbox, Field, FormGrid, Input, MoneyInput, Select, Textarea } from '@/components/ui/form'
import { RichTextEditor } from '@/components/ui/richtext'
import { Table, Td, Th } from '@/components/ui/table'
import { useToast } from '@/components/ui/toast'
import { AuditLogPanel } from '@/components/AuditLog'
import { ChainPreviewPanel } from '@/components/ChainPreview'
import { SendQuotationDialog } from '@/components/SendQuotationDialog'
import { AcceptancePreview } from '@/routes/AcceptQuotation'
import type {
  CustomerDetail,
  DiscountMode,
  LineSummary,
  MilestoneSummary,
  PaymentMilestoneBasis,
  Paged,
  PaymentShareMode,
  ProductDetail,
  ProductListItem,
  AcceptanceLinkStatus,
  QuotationApprovalView,
  QuotationDetail,
  QuotationStatus,
  SignedAcceptanceStatus,
  ResellerListItem,
  SectionSummary,
  SubmitBlocker,
} from '@/types/api'

/**
 * The statuses a payment can be recorded against, mirroring the API's own check.
 *
 * A payment is refused against anything the customer has not signed. Accepted counts as
 * well as Won, because a deposit often arrives before somebody remembers to mark the
 * outcome, and refusing it would only teach people to record payments late.
 */
const SIGNED: readonly QuotationStatus[] = ['Accepted', 'Won']

export function QuotationBuilder() {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const { can, user } = useAuth()
  const toast = useToast()

  const [blockers, setBlockers] = useState<SubmitBlocker[]>([])
  const [addingSection, setAddingSection] = useState(false)
  const [addingLineTo, setAddingLineTo] = useState<SectionSummary | undefined>()
  const [outcome, setOutcome] = useState<OutcomeChoice | null>(null)
  const [cancelling, setCancelling] = useState(false)
  const [reEditing, setReEditing] = useState(false)
  const [cloning, setCloning] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [deletingWon, setDeletingWon] = useState(false)
  const [decision, setDecision] = useState<'approve' | 'reject' | undefined>()
  const [preview, setPreview] = useState(false)
  const [sending, setSending] = useState(false)
  const [tab, setTab] = useState<
    'document' | 'schedule' | 'approval' | 'terms' | 'acceptance' | 'history'
  >('document')
  const [busy, setBusy] = useState(false)

  const quotation = useAsync<QuotationDetail>(
    (signal) => api.get<QuotationDetail>(`/quotations/${id}`, undefined, signal),
    [id],
  )

  const approval = useAsync<QuotationApprovalView>(
    (signal) => api.get<QuotationApprovalView>(`/quotations/${id}/approval`, undefined, signal),
    [id, quotation.data?.status],
  )

  function refresh() {
    quotation.reload()
    approval.reload()
  }

  async function act(run: () => Promise<unknown>, success: string) {
    setBusy(true)
    setBlockers([])

    try {
      await run()
      toast.show(success, 'success')
      refresh()
    } catch (cause) {
      if (cause instanceof ApiError) {
        // The submit endpoint answers with every blocker at once rather than the
        // first one. Showing them one at a time turns one fix into five round trips.
        if (cause.blockers.length > 0) setBlockers([...cause.blockers])
        toast.show(cause.message, 'error')
      } else {
        toast.show('Something went wrong', 'error')
      }
    } finally {
      setBusy(false)
    }
  }

  // Only the very first load replaces the screen. A reload after a save keeps what is
  // already on it and puts the spinner over the top - blanking a page somebody just
  // saved is the same jump as a loading block pushing a listing down.
  if (quotation.loading && !quotation.data) return <Loading />
  if (quotation.error)
    return <ErrorState title={quotation.error.title} detail={quotation.error.detail} onRetry={quotation.reload} />

  const data = quotation.data
  if (!data) return null

  const look = presentation(data.status)

  // A draft that went to the customer and came back for changes. "Nobody outside has seen
  // it" is the one thing that is not true of it.
  const hint =
    data.status === 'Draft' && data.sentAt
      ? "Taken back for changes. The customer's link no longer works - submit for approval, then send it again."
      : look.hint
  const editable = data.isEditable && can('quotation.section.manage')
  const canDelete = can('quotation.delete.any') || (data.preparedBy.id === user?.id && can('quotation.delete.own'))

  // A separate permission, and deliberately not a wider delete: taking a won deal out of
  // the revenue figures is the Owner's, and holding it says nothing about deleting drafts.
  const canDeleteWon = can('quotation.delete.won')

  // What the document prints as VALIDITY, worked out the same way the renderer does: the
  // distance between the two dates, never a stored number. A rep moving the deadline sees
  // the period the customer will read, on the same line, before saving anything.
  const validityDays =
    data.issueDate && data.validUntil
      ? Math.max(
          0,
          Math.round(
            (Date.parse(`${data.validUntil}T00:00:00Z`) - Date.parse(`${data.issueDate}T00:00:00Z`)) /
              86_400_000,
          ),
        )
      : null

  // The server has always answered "may this person act on this, right now". Nothing
  // read the answer outside the inbox.
  const pending = approval.data?.current
  const deleted = data.deletedAt !== null

  // Four controls at most, in any state: what reads, the state's frequent verb, a menu for
  // the rare and the destructive, and one primary. What replaced the previous arrangement -
  // four groups separated by a slightly wider gap - is a real divider before the primary,
  // because eight pixels against six is not a separator anybody sees.
  const showOutcome = canRecordOutcome(data.status) && can('quotation.markoutcome')

  // The only way out of an acceptance. Offered on anything still open, because a deal
  // called off before it was ever sent is as real as one called off after signature - and
  // on a draft the customer was already sent, which is not deletable and needs a way out
  // that says why.
  const showCancel = canCancel(data.status, data.sentAt) && can('quotation.cancel')
  const showWithdraw = canWithdraw(data.status)
  const showDelete = canDeleteDraft(data.status, data.sentAt) && canDelete

  // The same route as Withdraw, and the same people: the preparer, or whoever edits for the
  // team. Asked here so the menu does not offer something that only ever answers 403.
  const showReEdit =
    canReEdit(data.status) &&
    can('quotation.withdraw.own') &&
    (data.preparedBy.id === user?.id || can('quotation.edit.team'))

  // Only a won deal, and only after it is won: everything still live is cancelled instead,
  // which keeps it answering for itself rather than disappearing.
  const showDeleteWon = data.status === 'Won' && canDeleteWon
  const showSubmit = canSubmit(data.status) && can('quotation.submit')
  const showSend = canSend(data.status) && can('quotation.send')
  const showPrimary = Boolean(pending?.canIActNow) || showSubmit || showSend

  // Rare, destructive, or both. Every one of these was a button of its own in the row,
  // drawn exactly as loudly as the action the rep opened the page to perform.
  const menu: ActionMenuItem[] = [
    // Available whatever state this one is in. The reason to copy a quotation is usually
    // that it worked.
    ...(can('quotation.create') ? [{ label: 'Clone', onSelect: () => setCloning(true) }] : []),

    // The deal handed over in a meeting was still sent, and recording that must not require
    // pretending to email somebody. Named for what it does not do, because beside a button
    // that emails, "Mark as sent" reads as the same act.
    ...(showSend
      ? [{
          label: 'Mark as sent without emailing',
          onSelect: () => void act(() => api.post(`/quotations/${id}/send`), 'Recorded as sent'),
        }]
      : []),

    // A correction to something already approved. In the menu rather than the row: it is
    // rare, and on a sent quotation it takes the customer's link away.
    ...(showReEdit ? [{ label: 'Re-edit...', onSelect: () => setReEditing(true) }] : []),

    // Only a draft, and only for whoever may delete one. A quotation anybody has seen is
    // cancelled instead - it has a number, and a number that answers nothing is worse than
    // one that answers "cancelled".
    ...(showDelete
      ? [{ label: 'Delete draft...', danger: true, onSelect: () => setDeleting(true) }]
      : []),

    ...(showCancel
      ? [{ label: 'Cancel quotation...', danger: true, onSelect: () => setCancelling(true) }]
      : []),

    // The deal that is not a deal - a demonstration, a test, the same order numbered twice.
    // Nothing is destroyed; it stops counting, and says why.
    ...(showDeleteWon
      ? [{ label: 'Delete quotation...', danger: true, onSelect: () => setDeletingWon(true) }]
      : []),
  ]

  return (
    <>
      <PageHeader
        eyebrow={data.quotationNo ?? 'Not numbered yet'}
        title={data.title || data.customer.name}
        hint={hint}
        back={{ label: 'All quotations', onClick: () => navigate('/quotations') }}
        actions={
          deleted ? (
            /* A deleted quotation offers one action, and it is the one that undoes the
               deletion. Printing, cloning or submitting something that is out of the
               listing either produces a document nobody can trace back or quietly
               resurrects it through a side door. */
            (data.status === 'Won' ? canDeleteWon : canDelete) && (
              <Button
                variant="primary"
                loading={busy}
                onClick={() => void act(() => api.post(`/quotations/${id}/restore`), 'Back in the listing')}
              >
                Restore
              </Button>
            )
          ) : (
            <>
              {/* Everything left of the divider commits nothing outside this page. */}
              {can('quotation.pdf.generate') && (
                <Button onClick={() => setPreview(true)}>Print</Button>
              )}

              {/* One control with two answers rather than a dialog that asked which. Whoever
                  presses this already knows how the deal went; the question was a step
                  between them and saying so. Opens under its own left edge - hung from the
                  right it would lie across Print. */}
              {showOutcome && (
                <ActionMenu text="Record outcome" align="left" items={outcomeChoices(data.status, setOutcome)} />
              )}

              {showWithdraw && (
                <Button
                  loading={busy}
                  onClick={() => void act(() => api.post(`/quotations/${id}/withdraw`), 'Back to draft')}
                >
                  Withdraw to draft
                </Button>
              )}

              <ActionMenu items={menu} />

              {showPrimary && (
                <>
                  {/* A real rule rather than a wider gap, and the only one in the row: it
                      separates the two things that differ in kind. */}
                  <span aria-hidden className="mx-1 h-6 w-px self-center bg-line" />

                  {/* Where an approver actually approves. The Approvals inbox was the only
                      place these existed, which made the notification's own link a dead end:
                      it lands on the quotation, which is the thing you have to read before
                      deciding, and then offered nothing to decide with.

                      Send back stays inline beside Approve rather than moving into the menu.
                      They are two answers to one question, and burying either makes the other
                      look like the only option. */}
                  {pending?.canIActNow && (
                    <>
                      <Button onClick={() => setDecision('reject')}>Send back</Button>
                      <Button variant="primary" onClick={() => setDecision('approve')}>
                        Approve
                      </Button>
                    </>
                  )}

                  {showSubmit && (
                    <Button
                      variant="primary"
                      loading={busy}
                      onClick={() =>
                        void act(async () => {
                          const result = await api.post<{ firstApproverLabel: string; approverCount: number }>(
                            `/quotations/${id}/submit`,
                          )
                          toast.show(
                            `Submitted to ${result.firstApproverLabel}, ${result.approverCount} approver${
                              result.approverCount === 1 ? '' : 's'
                            } in the chain`,
                            'success',
                          )
                        }, 'Submitted for approval')
                      }
                    >
                      Submit for approval
                    </Button>
                  )}

                  {showSend && (
                    <Button variant="primary" onClick={() => setSending(true)}>
                      {data.status === 'Sent' ? 'Send again' : 'Send to customer'}
                    </Button>
                  )}
                </>
              )}
            </>
          )
        }
      />

      {deleted && (
        <Card className="mb-5 border-crit/30 bg-crit-wash">
          <div className="px-5 py-4">
            <p className="font-medium text-crit">Deleted</p>
            <p className="mt-1 text-[13px] text-ink-2">
              Taken out {relative(data.deletedAt)}. It is read-only until it is restored, and
              it appears only under the Deleted status filter.
              {data.status === 'Won' && (
                <> It counts towards no figure and no partner's commission while it is out.</>
              )}
            </p>

            {/* The reason, quoted rather than paraphrased. It is the only record of why a
                won deal stopped counting, and the person reading this is usually the person
                asking that question. */}
            {data.deletedReason && (
              <p className="mt-2 text-[13px] text-ink-2">
                <span className="text-muted">Reason: </span>
                {data.deletedReason}
              </p>
            )}
          </div>
        </Card>
      )}

      <div className="mb-5 flex flex-wrap items-center gap-2">
        <Badge tone={look.tone}>{look.label}</Badge>
        {data.revisionNo > 1 && <Badge>Revision {data.revisionNo}</Badge>}
        <Badge tone="accent">{data.documentStatus}</Badge>
        {data.terms.hasOverride && <Badge tone="warning">Customer-amended terms</Badge>}
        <span className="text-[13px] text-muted">
          {data.customer.name}
          {data.contact ? ` · ${data.contact.name}` : ''} · prepared by {data.preparedBy.name}
        </span>
      </div>

      {blockers.length > 0 && (
        <Card className="mb-5 border-crit/30 bg-crit-wash">
          <div className="px-5 py-4">
            <p className="font-medium text-crit">Not ready to submit</p>
            <ul className="mt-2 flex flex-col gap-1 text-[13px] text-ink-2">
              {blockers.map((blocker) => (
                <li key={blocker.code}>· {blocker.message}</li>
              ))}
            </ul>
          </div>
        </Card>
      )}

      <Tabs
        active={tab}
        onSelect={setTab}
        tabs={[
          { id: 'document', label: 'Document', count: data.sections.length },
          // "Payments", not "Payment schedule". What the customer agreed to pay and what
          // actually arrived are the same question asked twice, and the answer nobody has
          // without both - how much is still outstanding - only exists on one tab.
          { id: 'schedule', label: 'Payments', count: data.paymentSchedule.milestones.length },
          { id: 'approval', label: 'Approval' },
          { id: 'terms', label: 'Terms' },
          { id: 'acceptance', label: 'Acceptance' },
          { id: 'history', label: 'History' },
        ]}
      />

      {tab === 'document' && (
      <div className="grid gap-5 lg:grid-cols-[1fr_320px]">
        <div className="flex min-w-0 flex-col gap-5">
          <Card>
            <CardHeader
              title="Cover"
              hint="The title and opening paragraph the customer reads first."
            />
            <div className="grid gap-4 p-5">
              <Field label="Title">
                {(fieldId) => (
                  <Input
                    id={fieldId}
                    defaultValue={data.title ?? ''}
                    disabled={!data.isEditable}
                    onBlur={(event) =>
                      event.target.value !== (data.title ?? '') &&
                      void act(
                        () => api.patch(`/quotations/${id}`, { title: event.target.value }),
                        'Title saved',
                      )
                    }
                  />
                )}
              </Field>
              <Field
                label="Introduction"
                hint="Plain text. A blank line starts a new paragraph on the document."
              >
                {/* Plain, like the title above it. The stored value is still the HTML the
                    document renders - QuestPDF is handed paragraphs, not a string - so the
                    text is converted on the way in and out and neither the API nor the PDF
                    has to know. See lib/richText.ts. */}
                {(fieldId) => {
                  const typed = htmlToPlainText(data.introductionHtml)

                  return (
                    <Textarea
                      id={fieldId}
                      rows={4}
                      defaultValue={typed}
                      disabled={!data.isEditable}
                      placeholder="The opening paragraph the customer reads first."
                      onBlur={(event) =>
                        event.target.value !== typed &&
                        void act(
                          () =>
                            api.patch(`/quotations/${id}`, {
                              introductionHtml: plainTextToHtml(event.target.value),
                            }),
                          'Introduction saved',
                        )
                      }
                    />
                  )
                }}
              </Field>
            </div>
          </Card>

          {data.sections.length === 0 ? (
            <Card>
              <EmptyState
                title="No solution blocks yet"
                hint="A quotation is blocks, not flat lines: each one is a product, its narrative, its module bullets and its pricing ladder."
                action={
                  editable && (
                    <Button variant="primary" onClick={() => setAddingSection(true)}>
                      Add the first block
                    </Button>
                  )
                }
              />
            </Card>
          ) : (
            data.sections.map((section, index) => (
              <SectionCard
                key={section.id}
                section={section}
                quotation={data}
                editable={editable}
                isFirst={index === 0}
                isLast={index === data.sections.length - 1}
                busy={busy}
                onAddLine={() => setAddingLineTo(section)}
                onAct={act}
                sectionIds={data.sections.map((s) => s.id)}
                quotationId={id}
              />
            ))
          )}

          {editable && data.sections.length > 0 && (
            <div>
              <Button onClick={() => setAddingSection(true)}>Add solution block</Button>
            </div>
          )}

        </div>

        <div className="flex flex-col gap-5">
          <ResellerCard
            quotation={data}
            quotationId={id}
            editable={editable}
            busy={busy}
            onAct={act}
          />
          <TotalsPanel quotation={data} />
        </div>
      </div>
      )}

      {tab === 'schedule' && (
        <div className="grid gap-5 lg:grid-cols-[1fr_320px]">
          <div className="flex min-w-0 flex-col gap-5">
            <PaymentScheduleCard
              quotation={data}
              quotationId={id}
              editable={editable}
              busy={busy}
              onAct={act}
            />

            {/* Money that has actually arrived, measured against the schedule above it.
                Only once the customer has signed: a payment is refused against anything
                unsigned, so offering the form earlier would be offering a refusal. */}
            {SIGNED.includes(data.status) && !deleted && (
              <QuotationPayments
                quotationId={id}
                milestones={data.paymentSchedule.milestones}
                canRecord
              />
            )}
          </div>

          {/* The totals stay in view: milestones divide the one-time figure, and being
              asked to divide a number that is on another tab is how a schedule ends up
              not adding up. */}
          <div className="flex flex-col gap-5">
            <TotalsPanel quotation={data} />
          </div>
        </div>
      )}

      {tab === 'approval' && (
        <ChainPreviewPanel view={approval.data} loading={approval.loading} />
      )}

      {tab === 'terms' && (
        <div className="flex flex-col gap-5">
          <TermsOverrideCard quotation={data} onChanged={refresh} />
          <Card>
            <CardHeader title="Terms and validity" />
            <div className="flex flex-col gap-3 px-5 py-4 text-[13px]">
              {/* Editable, because the deadline is the one commercial term on this tab the
                  rep actually negotiates - "can you hold that price to the end of the
                  quarter" is the usual reason a quotation is re-cut. The document's own
                  VALIDITY field and its acceptance clause are both the distance from the
                  issue date, so moving this moves all three and there is no second place
                  to keep in step. */}
              {editable ? (
                <div className="flex items-center justify-between gap-3">
                  <label htmlFor="valid-until" className="text-muted">
                    Valid until
                  </label>

                  <div className="flex items-center gap-3">
                    {validityDays !== null && (
                      <span className="text-[12px] text-muted">
                        {validityDays} day{validityDays === 1 ? '' : 's'}
                      </span>
                    )}

                    <Input
                      id="valid-until"
                      type="date"
                      className="w-[10.5rem]"
                      disabled={busy}
                      // The picker will not offer a day before the quotation was issued.
                      // The API refuses one anyway - a typed date reaches it either way -
                      // but being stopped at the control beats being told after the save.
                      min={data.issueDate ?? undefined}
                      defaultValue={data.validUntil ?? ''}
                      key={data.validUntil ?? ''}
                      onBlur={(event) =>
                        event.target.value !== (data.validUntil ?? '') &&
                        event.target.value !== '' &&
                        void act(
                          () => api.patch(`/quotations/${id}`, { validUntil: event.target.value }),
                          'Validity saved',
                        )
                      }
                    />
                  </div>
                </div>
              ) : (
                <Row label="Valid until" value={date(data.validUntil)} />
              )}

              <Row label="Issued" value={date(data.issueDate)} />
              <Row
                label="Terms"
                value={
                  data.terms.hasOverride
                    ? `Customer-amended: ${data.terms.overrideFileName ?? 'attached'}`
                    : data.terms.documentName
                      ? // Frozen once submitted; before that it is what this quotation would
                        // be issued under, which is what the preview PDF already appends.
                        `${data.terms.documentName} v${data.terms.versionNo}${
                          data.terms.documentVersionId ? '' : ' - attached at submit'
                        }`
                      : data.terms.notYetEffectiveFrom
                        ? `Published, but not in force until ${date(data.terms.notYetEffectiveFrom)}`
                        : 'None published for this country'
                }
              />

              {/* Both of these stop the submit. Said here, while the quotation is being
                  written, rather than at the moment somebody tries to send it. */}
              {!data.terms.hasOverride && !data.terms.documentName && (
                <p className="rounded-md bg-crit-wash px-3 py-2 text-[12.5px] text-crit">
                  {data.terms.notYetEffectiveFrom
                    ? `Nothing can be issued in ${data.country.name} until those terms take effect. Publish a version effective on or before the issue date.`
                    : `${data.country.name} has no published terms document, so it cannot issue a quotation. Ask an administrator to upload one.`}
                </p>
              )}
              {/* The checksum is what proves which file was issued, years later. */}
              {data.terms.sha256 && (
                <Row label="Checksum" value={`${data.terms.sha256.slice(0, 12)}…`} mono />
              )}
            </div>
          </Card>
        </div>
      )}

      {tab === 'acceptance' && (
        <div className="flex flex-col gap-5">
          <AcceptanceLinkCard quotation={data} quotationId={id} />
          <SignedAcceptanceCard quotation={data} quotationId={id} busy={busy} onAct={act} />

          {/* What the customer is looking at, read-only. A rep checking it should not have
              to mint a live signing link to find out what their own quotation looks like. */}
          <Card>
            <CardHeader
              title="What the customer sees"
              hint="The acceptance page, exactly as it renders for them."
            />
            <div className="px-2 pb-2">
              <AcceptancePreview quotationId={id} />
            </div>
          </Card>
        </div>
      )}

      {tab === 'history' && (
        <AuditLogPanel entityType="Quotation" entityId={id!} className="" />
      )}

      <AddSection
        open={addingSection}
        quotationId={id}
        onClose={() => setAddingSection(false)}
        onAdded={() => {
          setAddingSection(false)
          refresh()
        }}
      />

      <AddLine
        section={addingLineTo}
        quotation={data}
        onClose={() => setAddingLineTo(undefined)}
        onAdded={() => {
          setAddingLineTo(undefined)
          refresh()
        }}
      />

      {decision !== undefined && pending && (
        <DecideDialog
          requestId={pending.id}
          approve={decision === 'approve'}
          isOverride={pending.wouldBeAnOverride}
          summary={`${data.quotationNo ?? 'Unnumbered draft'} · ${data.customer.name} · ${money(
            data.totals.grandTotal,
            data.country.currencySymbol,
            data.country.currencyDecimals,
          )}`}
          onClose={() => setDecision(undefined)}
          onDone={() => {
            setDecision(undefined)
            refresh()
          }}
        />
      )}

      {deleting && (
        <Dialog
          open
          title="Delete this draft?"
          description="It comes out of the listing and becomes read-only. Nothing is destroyed - it stays under the Deleted status filter, and you can restore it."
          onClose={() => setDeleting(false)}
          footer={
            <>
              <Button onClick={() => setDeleting(false)}>Keep it</Button>
              <Button
                variant="danger"
                loading={busy}
                onClick={() =>
                  void act(async () => {
                    await api.del(`/quotations/${id}`)
                    setDeleting(false)
                  }, 'Deleted')
                }
              >
                Delete
              </Button>
            </>
          }
        >
          <p className="text-[13px] text-ink-2">
            {data.quotationNo
              ? `${data.quotationNo} for ${data.customer.name}.`
              : `An unnumbered draft for ${data.customer.name}.`}
          </p>
        </Dialog>
      )}

      {cloning && (
        <CloneQuotation
          quotation={data}
          onClose={() => setCloning(false)}
          onCloned={(cloneId) => {
            setCloning(false)
            navigate(`/quotations/${cloneId}`)
          }}
        />
      )}

      {outcome && (
        <RecordOutcome
          outcome={outcome}
          quotationId={id}
          onClose={() => setOutcome(null)}
          onRecorded={() => {
            setOutcome(null)
            refresh()
          }}
        />
      )}

      {reEditing && (
        <Dialog
          open
          title="Re-edit this quotation?"
          description="It goes back to draft so you can change it. It then has to be submitted and approved again before it can be sent."
          onClose={() => setReEditing(false)}
          footer={
            <>
              <Button onClick={() => setReEditing(false)}>Keep it</Button>
              <Button
                variant="primary"
                loading={busy}
                onClick={() =>
                  void act(async () => {
                    await api.post(`/quotations/${id}/withdraw`)
                    setReEditing(false)
                  }, 'Back to draft - submit it for approval when you are done')
                }
              >
                Re-edit
              </Button>
            </>
          }
        >
          {/* Said only when it is true. An approved quotation nobody has sent costs nothing
              to take back; a sent one costs the customer the link in their inbox. */}
          {data.status === 'Sent' ? (
            <p className="text-[13px] text-ink-2">
              {data.customer.name} has been sent this quotation. Their link stops working now:
              opening it shows <span className="font-medium">Quotation is expired or no longer
              valid</span>, and nothing you change is visible to them. Send it again once the
              revision is approved.
            </p>
          ) : (
            <p className="text-[13px] text-ink-2">
              This revision has not been sent to {data.customer.name}, so nobody outside
              Codemax is affected.
            </p>
          )}
        </Dialog>
      )}

      <CancelQuotation
        open={cancelling}
        quotation={data}
        quotationId={id}
        onClose={() => setCancelling(false)}
        onCancelled={() => {
          setCancelling(false)
          refresh()
        }}
      />

      <DeleteWonQuotation
        open={deletingWon}
        quotation={data}
        quotationId={id}
        onClose={() => setDeletingWon(false)}
        onDeleted={() => {
          setDeletingWon(false)
          refresh()
        }}
      />

      <DocumentPreview open={preview} quotation={data} onClose={() => setPreview(false)} />

      {sending && (
        <SendQuotationDialog
          quotationId={id!}
          onClose={() => setSending(false)}
          onSent={() => {
            setSending(false)
            refresh()
          }}
        />
      )}
    </>
  )
}

/**
 * A terms document the customer amended, attached to this quotation alone.
 *
 * It never becomes a country default and it always adds the Country Manager to the
 * approval chain: amended terms change what Codemax is agreeing to, so somebody senior
 * reads them before the quotation goes anywhere.
 */
function TermsOverrideCard({
  quotation,
  onChanged,
}: {
  quotation: QuotationDetail
  onChanged: () => void
}) {
  const { can } = useAuth()
  const toast = useToast()
  const [attaching, setAttaching] = useState(false)
  const [busy, setBusy] = useState(false)

  const allowed = can('quotation.terms.override') && quotation.isEditable

  if (!quotation.terms.hasOverride && !allowed) return null

  async function remove() {
    setBusy(true)
    try {
      await api.del(`/quotations/${quotation.id}/terms-override`)
      toast.show('Back to the country default terms', 'success')
      onChanged()
    } catch (cause) {
      toast.show(cause instanceof ApiError ? cause.message : 'Could not remove it', 'error')
    } finally {
      setBusy(false)
    }
  }

  async function download() {
    try {
      const blob = await requestBlob(`/quotations/${quotation.id}/terms-override/download`)
      const href = URL.createObjectURL(blob)
      const anchor = document.createElement('a')

      anchor.href = href
      anchor.download = quotation.terms.overrideFileName ?? 'amended-terms.pdf'
      anchor.click()
      URL.revokeObjectURL(href)
    } catch (cause) {
      toast.show(cause instanceof ApiError ? cause.message : 'Could not download it', 'error')
    }
  }

  return (
    <>
      <Card>
        <CardHeader
          title="Customer-amended terms"
          hint="Attaching one always adds the Country Manager to the approval chain."
        />

        <div className="px-5 py-4 text-[13px]">
          {quotation.terms.hasOverride ? (
            <div className="flex flex-col gap-3">
              <div>
                <p className="font-medium">{quotation.terms.overrideFileName}</p>
                {quotation.terms.overrideReason && (
                  <p className="mt-1 text-muted">“{quotation.terms.overrideReason}”</p>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" onClick={() => void download()}>
                  Download
                </Button>
                {allowed && (
                  <>
                    <Button size="sm" onClick={() => setAttaching(true)}>
                      Replace
                    </Button>
                    <Button size="sm" variant="danger" loading={busy} onClick={() => void remove()}>
                      Remove
                    </Button>
                  </>
                )}
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              <p className="text-muted">
                This quotation carries the country's published terms. Attach an amended
                document only if the customer's legal team changed it.
              </p>
              <div>
                <Button size="sm" onClick={() => setAttaching(true)}>
                  Attach amended terms
                </Button>
              </div>
            </div>
          )}
        </div>
      </Card>

      <AttachOverride
        open={attaching}
        quotationId={quotation.id}
        onClose={() => setAttaching(false)}
        onAttached={() => {
          setAttaching(false)
          onChanged()
        }}
      />
    </>
  )
}

function AttachOverride({
  open,
  quotationId,
  onClose,
  onAttached,
}: {
  open: boolean
  quotationId: string
  onClose: () => void
  onAttached: () => void
}) {
  const toast = useToast()
  const [file, setFile] = useState<File | undefined>()
  const [reason, setReason] = useState('')
  const [saving, setSaving] = useState(false)

  async function attach() {
    if (!file || !reason.trim()) return

    setSaving(true)
    try {
      const form = new FormData()
      form.append('file', file)
      form.append('reason', reason.trim())

      await postForm(`/quotations/${quotationId}/terms-override`, form)

      toast.show('Attached. The Country Manager is now in the chain.', 'success')
      onAttached()
      setFile(undefined)
      setReason('')
    } catch (cause) {
      toast.show(cause instanceof ApiError ? cause.message : 'Could not attach it', 'error')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog
      open={open}
      title="Attach amended terms"
      description="For this quotation only. It never becomes a country default, and the document is badged so nobody mistakes it for the standard one."
      onClose={onClose}
      footer={
        <>
          <Button size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            variant="primary"
            loading={saving}
            disabled={!file || reason.trim().length === 0}
            onClick={() => void attach()}
          >
            Attach
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label="Amended document" required hint="PDF only, up to 20 MB.">
          {(id) => (
            <input
              id={id}
              type="file"
              accept="application/pdf"
              onChange={(event) => setFile(event.target.files?.[0])}
              className="w-full rounded-md border border-line bg-surface px-3 py-2 text-sm"
            />
          )}
        </Field>

        <Field
          label="What the customer changed"
          required
          hint="The Country Manager reads this before the document itself."
        >
          {(id) => <Textarea id={id} value={reason} onChange={(event) => setReason(event.target.value)} />}
        </Field>
      </div>
    </Dialog>
  )
}

/**
 * The link the customer signs through.
 *
 * <p>
 * The token is readable exactly once, at the moment it is minted: only its hash is stored,
 * so there is nothing to show a second time. Re-minting is how somebody gets a link back
 * and it kills the previous one - two live links to one quotation would be two things to
 * remember to revoke.
 * </p>
 */
/**
 * The signing link, as a record rather than a control.
 *
 * <p>
 * A link is created by sending the quotation and by nothing else: the outbox handler mints
 * one when it resolves the placeholder in the mail, so the link that exists is always the
 * link a customer was actually given. Minting by hand produced links nobody had been sent,
 * and revoking by hand produced quotations marked Sent whose customer could no longer act
 * on them - both states this card could show and neither of which anybody could explain.
 * </p>
 *
 * Re-sending replaces it, which is the remedy for a mail that went to the wrong address.
 */
function AcceptanceLinkCard({
  quotation,
  quotationId,
}: {
  quotation: QuotationDetail
  quotationId: string
}) {
  const { can } = useAuth()

  const status = useAsync<AcceptanceLinkStatus>(
    (signal) =>
      api.get<AcceptanceLinkStatus>(`/quotations/${quotationId}/acceptance-link`, undefined, signal),
    [quotationId, quotation.status, quotation.sentAt],
  )

  const link = status.data

  // Sending queues the mail; the link is minted when the outbox delivers it, which is up to
  // a poll interval later. So closing the Send dialog and reloading the quotation is not
  // enough - at that moment there is genuinely nothing to show, and the card would sit on
  // "no link yet" until somebody navigated away and back.
  const awaiting = awaitingLink(quotation.sentAt, link)

  useEffect(() => {
    if (!awaiting) return

    // Stops on its own. A mail that permanently fails mints nothing, and a card that polled
    // forever would be a request every few seconds for the life of the tab.
    let attempts = 0
    const timer = setInterval(() => {
      attempts += 1
      if (attempts > 20) {
        clearInterval(timer)
        return
      }

      status.reload()
    }, 3_000)

    return () => clearInterval(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [awaiting, quotationId, quotation.sentAt])

  if (!can('quotation.send')) return null

  return (
    <Card>
      <CardHeader
        title="Signing link"
        hint="Created when the quotation is emailed, and replaced each time it is sent again."
      />

      <div className="flex flex-col gap-3 px-5 py-4 text-[13px]">
        {awaiting && (
          <p className="flex items-center gap-2 rounded-md bg-wash px-3 py-2 text-[12.5px] text-muted">
            <Spinner className="size-3.5" label={null} />
            Preparing the link. It is created as the mail goes out.
          </p>
        )}

        {link?.exists ? (
          <>
            <Row label="Created" value={dateTime(link.issuedAt)} />
            <Row label="Expires" value={link.expiresOn ? date(link.expiresOn) : 'With the quotation'} />
            <Row
              label="State"
              value={
                link.usedAt
                  ? `Used ${dateTime(link.usedAt)}`
                  : link.revokedAt
                    ? `Revoked ${dateTime(link.revokedAt)}`
                    : 'Live'
              }
            />
          </>
        ) : (
          !awaiting && (
            <p className="text-muted">
              {canSend(quotation.status)
                ? 'No link yet. Sending this quotation by email creates one and gives it to the customer.'
                : `A link is created when the quotation is emailed, which it cannot be while it is ${presentation(quotation.status).label.toLowerCase()}.`}
            </p>
          )
        )}
      </div>
    </Card>
  )
}

/**
 * Whether a link is owed and has not arrived.
 *
 * <p>
 * True from the moment a quotation is sent until the link that send produces appears. The
 * comparison is against the send, not against the previous link: re-sending an already-sent
 * quotation mints a replacement, and a card that only asked "is there a link" would go on
 * showing the one that send had just revoked.
 * </p>
 *
 * Exported so the rule can be asserted without a clock or a network.
 */
export function awaitingLink(
  sentAt: string | null | undefined,
  link: { exists: boolean; issuedAt: string | null } | undefined,
): boolean {
  if (!sentAt) return false

  // Not loaded yet is not the same as owed. The card would flash its waiting line on every
  // visit to a quotation that has had a link for weeks.
  if (link === undefined) return false

  if (!link.exists || link.issuedAt === null) return true

  return new Date(link.issuedAt).getTime() < new Date(sentAt).getTime()
}

/**
 * The copy a customer chopped and signed on paper.
 *
 * <p>
 * Most customers here still sign that way, and a system that only recognised an online
 * signature would leave every one of those deals unable to be marked won. Uploading this
 * does not move the quotation to Accepted - Accepted means the customer told this system so
 * themselves - but it is the same evidence as far as recording a win is concerned.
 * </p>
 */
function SignedAcceptanceCard({
  quotation,
  quotationId,
  busy,
  onAct,
}: {
  quotation: QuotationDetail
  quotationId: string
  busy: boolean
  onAct: (run: () => Promise<unknown>, success: string) => Promise<void>
}) {
  const { can } = useAuth()
  const [uploading, setUploading] = useState(false)

  const status = useAsync<SignedAcceptanceStatus>(
    (signal) =>
      api.get<SignedAcceptanceStatus>(
        `/quotations/${quotationId}/signed-acceptance`, undefined, signal),
    [quotationId, quotation.status],
  )

  if (!can('quotation.acceptance.upload')) return null

  const attached = status.data?.exists === true
  const signedOnline = quotation.status === 'Accepted'

  return (
    <Card>
      <CardHeader
        title="Signed copy"
        hint="The quotation the customer printed, chopped and signed. Needed before this can be marked won."
      />

      <div className="flex flex-col gap-3 px-5 py-4 text-[13px]">
        {signedOnline ? (
          <p className="rounded-md bg-wash px-3 py-2 text-[12.5px] text-muted">
            The customer accepted this online, so there is already a signature on record.
            Nothing needs to be uploaded.
          </p>
        ) : (
          <>
            <p className="text-muted">
              {attached
                ? `Attached: ${status.data?.fileName ?? 'signed copy'}.`
                : 'Nothing attached yet. A win cannot be recorded without a signature, online or on paper.'}
            </p>

            <div className="flex flex-wrap items-center gap-2">
              <label className="inline-flex">
                <span
                  className={cx(
                    'cursor-pointer rounded-md border border-line px-3 py-1.5 text-[12.5px] font-medium',
                    (busy || uploading) && 'pointer-events-none opacity-50',
                  )}
                >
                  {attached ? 'Replace' : 'Upload'} signed copy
                </span>
                <input
                  type="file"
                  accept="application/pdf"
                  className="sr-only"
                  disabled={busy || uploading}
                  onChange={(event) => {
                    const file = event.target.files?.[0]
                    event.target.value = ''
                    if (!file) return

                    setUploading(true)

                    void onAct(async () => {
                      const body = new FormData()
                      body.append('file', file)

                      await postForm(`/quotations/${quotationId}/signed-acceptance`, body)
                      status.reload()
                    }, 'Signed copy attached').finally(() => setUploading(false))
                  }}
                />
              </label>

              {attached && (
                <a
                  href={`${BASE}/quotations/${quotationId}/signed-acceptance/download`}
                  className="text-[12.5px] font-medium text-teal underline-offset-2 hover:underline"
                >
                  Download
                </a>
              )}
            </div>
          </>
        )}
      </div>
    </Card>
  )
}

/**
 * Calling a quotation off.
 *
 * <p>
 * Distinct from recording a loss, and the only way back out of an acceptance. A loss says
 * the customer said no; this says the thing is not happening. Cancelled sits in neither the
 * won nor the lost denominator, so a deal called off after signature does not become a
 * mark against a win rate it was never competing in.
 * </p>
 */
function CancelQuotation({
  open,
  quotation,
  quotationId,
  onClose,
  onCancelled,
}: {
  open: boolean
  quotation: QuotationDetail
  quotationId: string
  onClose: () => void
  onCancelled: () => void
}) {
  const toast = useToast()
  const [reason, setReason] = useState('')
  const [saving, setSaving] = useState(false)

  const signed = quotation.status === 'Accepted'

  async function cancel() {
    setSaving(true)

    try {
      await api.post(`/quotations/${quotationId}/cancel`, { reason: reason.trim() })
      toast.show('Quotation cancelled', 'success')
      setReason('')
      onCancelled()
    } catch (cause) {
      toast.show(cause instanceof ApiError ? cause.message : 'Could not cancel it', 'error')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog
      open={open}
      title="Cancel this quotation"
      description="It leaves the pipeline entirely. This counts as neither won nor lost."
      onClose={onClose}
      footer={
        <>
          <Button size="sm" onClick={onClose}>
            Keep it
          </Button>
          <Button
            size="sm"
            variant="danger"
            loading={saving}
            disabled={reason.trim().length === 0}
            onClick={() => void cancel()}
          >
            Cancel quotation
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {/* Said plainly. Cancelling something the customer put their name to is not the
            same act as cancelling a draft nobody has seen. */}
        {signed && (
          <p className="rounded-md bg-crit-wash px-3 py-2 text-[12.5px] text-crit">
            The customer has already accepted this quotation. Cancelling it undoes a
            signature they gave, and any live signing link stops working.
          </p>
        )}

        <Field
          label="Why is this being called off"
          required
          hint="Somebody will ask about this a year from now. Write it for them."
        >
          {(id) => (
            <Textarea id={id} value={reason} onChange={(event) => setReason(event.target.value)} />
          )}
        </Field>
      </div>
    </Dialog>
  )
}

/**
 * Takes a won deal out of every figure, with a reason.
 *
 * <p>
 * The deal that is not a deal: a demonstration, a test, the same order numbered twice. It
 * is not a cancellation - the customer did sign, and nothing here contradicts that - and it
 * is not a removal either. What it does is stop Codemax counting the deal, in the pipeline,
 * in a win rate, in a target, and in what a partner is owed.
 * </p>
 * <p>
 * Modelled on the cancel dialog rather than the draft's delete confirmation, because it is
 * the same kind of act: a closed record taken out of the count, and a sentence saying why.
 * </p>
 */
function DeleteWonQuotation({
  open,
  quotation,
  quotationId,
  onClose,
  onDeleted,
}: {
  open: boolean
  quotation: QuotationDetail
  quotationId: string
  onClose: () => void
  onDeleted: () => void
}) {
  const toast = useToast()
  const [reason, setReason] = useState('')
  const [saving, setSaving] = useState(false)

  async function remove() {
    setSaving(true)

    try {
      await api.post(`/quotations/${quotationId}/delete`, { reason: reason.trim() })
      toast.show('Taken out of the figures', 'success')
      setReason('')
      onDeleted()
    } catch (cause) {
      toast.show(cause instanceof ApiError ? cause.message : 'Could not delete it', 'error')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog
      open={open}
      title="Delete this quotation"
      description="It stays in the system and stays won. It stops counting."
      onClose={onClose}
      footer={
        <>
          <Button size="sm" onClick={onClose}>
            Keep it
          </Button>
          <Button
            size="sm"
            variant="danger"
            loading={saving}
            disabled={reason.trim().length === 0}
            onClick={() => void remove()}
          >
            Delete quotation
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <p className="text-[13px] text-ink-2">
          {quotation.quotationNo ?? 'This quotation'} for {quotation.customer.name} comes out of
          the pipeline, the win rate, every target and every export. A reseller stops seeing it,
          and commission on it that no statement has claimed stops being payable. Nothing is
          destroyed: it appears under the Deleted status filter and you can restore it.
        </p>

        {/* Said plainly, because it is the one thing this cannot undo. A partner already
            sent a statement carrying this deal keeps it, and the API refuses the deletion
            outright rather than quietly moving their figures. */}
        <p className="rounded-md bg-crit-wash px-3 py-2 text-[12.5px] text-crit">
          If a partner has already been sent a statement carrying commission on this deal,
          this is refused. Reverse the payments instead - that reverses the commission by the
          same arithmetic.
        </p>

        <Field
          label="Why is this being taken out of the figures"
          required
          hint="A test, a demonstration, the same order raised twice. Somebody will ask about this a year from now."
        >
          {(id) => (
            <Textarea id={id} value={reason} onChange={(event) => setReason(event.target.value)} />
          )}
        </Field>
      </div>
    </Dialog>
  )
}

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-muted">{label}</span>
      <span className={mono ? 'tabular text-[12px]' : 'text-right'}>{value}</span>
    </div>
  )
}

/**
 * The payment schedule: every figure the customer is asked to pay, in the order they are
 * asked for it, and when the subscription starts being charged.
 *
 * <p>
 * One list, two kinds of row. A row either divides the one-time fee - and those must add up
 * to it exactly - or takes the subscription in advance, which divides nothing and is
 * measured in months of it instead. Keeping both in one list is what lets an advance sit
 * between two instalments, which is where the business puts it.
 * </p>
 */
function PaymentScheduleCard({
  quotation,
  quotationId,
  editable,
  busy,
  onAct,
}: {
  quotation: QuotationDetail
  quotationId: string
  editable: boolean
  busy: boolean
  onAct: (run: () => Promise<unknown>, success: string) => Promise<void>
}) {
  const schedule = quotation.paymentSchedule
  const symbol = quotation.country.currencySymbol
  const places = quotation.country.currencyDecimals

  // Said here because the document says it against every amount: the milestones divide the
  // net one-time charges, so a rep reading this table is looking at figures the customer
  // will be invoiced tax on top of. A country with no tax rate is told nothing.
  const taxNote =
    quotation.country.taxRate > 0
      ? ` Every figure here is exclusive of ${quotation.country.taxLabel}, which the document prints against each amount.`
      : ''

  // Which row is being carried, and which row it is over. Held here rather than in the
  // row, because a drag is a fact about the list.
  const [dragging, setDragging] = useState<string | null>(null)
  const [over, setOver] = useState<string | null>(null)

  /**
   * Moves one row and writes the whole order back.
   *
   * One request for the list rather than a sort order per row: dragging the last stop to
   * the top changes every row between, and a browser that stops halfway through that many
   * requests leaves two rows claiming the same place.
   */
  function reorder(from: number, to: number) {
    const ids = schedule.milestones.map((m) => m.id)

    if (from === to || from < 0 || to < 0 || from >= ids.length || to >= ids.length) return

    const [moved] = ids.splice(from, 1)
    if (moved === undefined) return

    ids.splice(to, 0, moved)

    void onAct(
      () => api.put(`/quotations/${quotationId}/milestones/order`, { milestoneIdsInOrder: ids }),
      'Order saved',
    )
  }

  // Nothing to divide and nothing recurring to say anything about. Asking when a
  // subscription commences on a quotation that has no subscription is a question with no
  // answer, and dividing a fee of nothing is arithmetic nobody needs. On its own tab this
  // has to say so - an empty panel reads as a screen that failed to load.
  if (schedule.oneTimeTotal <= 0 && !schedule.hasRecurring) {
    return (
      <Card>
        <EmptyState
          title="Nothing to schedule yet"
          hint="A schedule divides the one-time charges and says when a subscription starts. Price a solution block first."
        />
      </Card>
    )
  }

  const shortfall = schedule.oneTimeTotal - schedule.scheduled

  return (
    <Card>
      <CardHeader
        title="Payment schedule"
        hint={`One-time rows divide the one-time charges. An advance takes months of the subscription up front and divides nothing.${taxNote}`}
        action={
          editable && (
            <Button
              size="sm"
              disabled={busy}
              onClick={() =>
                void onAct(
                  () =>
                    api.post(`/quotations/${quotationId}/milestones`, {
                      name: 'Payment',
                      // A quotation with nothing one-time to divide can only mean an
                      // advance, so the new row arrives as one rather than as a share of
                      // zero the rep then has to correct.
                      ...(schedule.oneTimeTotal > 0
                        ? {}
                        : { basis: 'SubscriptionAdvance', mode: 'Months', months: 1 }),
                    }),
                  'Milestone added',
                )
              }
            >
              Add milestone
            </Button>
          )
        }
      />

      {(schedule.oneTimeTotal > 0 || schedule.milestones.length > 0) && (
        <>
          <Table>
            <thead>
              <tr>
                {/* Always rendered: the rail is the sequence, so a read-only schedule
                    carries it too. */}
                <Th className="w-9" aria-label="Order" />
                <Th>Milestone</Th>
                <Th>Share</Th>
                <Th numeric>Amount</Th>
                {editable && <Th />}
              </tr>
            </thead>
            <tbody>
              {schedule.milestones.map((milestone, index) => (
                <MilestoneRow
                  key={milestone.id}
                  milestone={milestone}
                  index={index}
                  count={schedule.milestones.length}
                  symbol={symbol}
                  places={places}
                  editable={editable}
                  busy={busy}
                  onAct={onAct}
                  dragging={dragging === milestone.id}
                  over={over === milestone.id && dragging !== milestone.id}
                  onDragStart={() => setDragging(milestone.id)}
                  onDragOver={() => setOver(milestone.id)}
                  onDragEnd={() => {
                    setDragging(null)
                    setOver(null)
                  }}
                  onDrop={() => {
                    const from = schedule.milestones.findIndex((m) => m.id === dragging)
                    setDragging(null)
                    setOver(null)
                    reorder(from, index)
                  }}
                  onNudge={(delta) => reorder(index, index + delta)}
                />
              ))}
            </tbody>
          </Table>

          {schedule.milestones.length === 0 ? (
            <EmptyState
              title="No staged payments"
              hint="Without milestones the document says nothing about when the one-time charges are paid."
            />
          ) : (
            <div
              className={cx(
                'flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-t px-5 py-3 text-[12.5px]',
                schedule.addsUp ? 'border-line-soft text-muted' : 'border-crit/30 bg-crit-wash text-crit',
              )}
            >
              <span className="tabular">
                Scheduled {money(schedule.scheduled, symbol, places)} of{' '}
                {money(schedule.oneTimeTotal, symbol, places)}
                {/* Said out loud, because a rep looking at a row for money the figure
                    deliberately excludes would otherwise read the strip as broken. */}
                {schedule.milestones.some((m) => m.basis === 'SubscriptionAdvance') && (
                  <span className="text-muted"> · advances excluded</span>
                )}
              </span>

              {/* Named rather than "does not add up": the rep needs the figure to fix, and
                  the submit gate will refuse it in exactly these words. */}
              <span className="tabular font-medium">
                {schedule.addsUp
                  ? 'Adds up'
                  : shortfall > 0
                    ? `${money(shortfall, symbol, places)} unallocated`
                    : `${money(-shortfall, symbol, places)} over`}
              </span>
            </div>
          )}
        </>
      )}

      {schedule.hasRecurring && (
        <SubscriptionTerms
          quotation={quotation}
          quotationId={quotationId}
          editable={editable}
          busy={busy}
          onAct={onAct}
        />
      )}
    </Card>
  )
}

/**
 * What a row's share says in one phrase, for the read-only view.
 *
 * A sum shows nothing: the amount column already prints it, and repeating it as a share
 * would suggest there is a percentage behind it.
 */
function shareLabel(milestone: MilestoneSummary): string {
  if (milestone.mode === 'Months') {
    return `${milestone.months} month${milestone.months === 1 ? '' : 's'} in advance`
  }

  if (milestone.mode === 'Percent') {
    return percent(milestone.percent, 2)
  }

  return milestone.basis === 'SubscriptionAdvance' ? 'In advance' : '\u2014'
}

/**
 * The line between two stops.
 *
 * <p>
 * Drawn against the cell rather than between rows: the cell already spans the row's full
 * height, however tall a wrapped condition made it, so consecutive segments meet exactly.
 * The offsets are the marker's own geometry - it sits 4px down and is 20px across, so its
 * centre is 14px from the top and 18px from the left edge of a 36px cell with 16px of
 * padding.
 * </p>
 */
function Rail({ first, last }: { first: boolean; last: boolean }) {
  return (
    <>
      {!first && (
        <span aria-hidden className="absolute left-[26px] top-0 h-[14px] w-px -translate-x-1/2 bg-line" />
      )}
      {!last && (
        <span aria-hidden className="absolute bottom-0 left-[26px] top-[14px] w-px -translate-x-1/2 bg-line" />
      )}
    </>
  )
}

/**
 * The same offer, for somebody else.
 *
 * <p>
 * The customer is prompted rather than assumed, because the usual reason to copy a
 * quotation is that it worked and somebody else should see it. The copy carries the blocks
 * and the lines at the prices they were quoted at - a quotation is a snapshot, and a copy
 * that silently re-priced itself against today's catalogue would be a different offer
 * wearing the same blocks.
 * </p>
 *
 * It carries none of what says the original happened: no number, no approval, no frozen
 * terms, no outcome. It is a draft.
 */
export function CloneQuotation({
  quotation,
  onClose,
  onCloned,
}: {
  quotation: QuotationDetail
  onClose: () => void
  onCloned: (id: string) => void
}) {
  const toast = useToast()
  const [title, setTitle] = useState(quotation.title ?? '')
  const [saving, setSaving] = useState(false)

  // Undefined until somebody chooses, which is what lets the default below stand in.
  const [picked, setPicked] = useState<ComboboxOption>()

  // The quotation's own customer is the default, and it cannot always take the copy - this
  // may be the deal that was won just before the company was retired, and a copy is a new
  // quotation. So it is asked about rather than assumed: the picker only ever lists
  // customers that can be chosen, and a default that is not one of them would be a name in
  // the box that fails when Create draft is pressed.
  const own = useAsync<'offered' | 'inactive' | 'out of reach'>(
    async (signal) => {
      try {
        const found = await api.get<CustomerDetail>(`/customers/${quotation.customer.id}`, undefined, signal)

        return found.status === 'Active' ? 'offered' : 'inactive'
      } catch (cause) {
        // Not one this person may open, or gone. Either way it is not theirs to copy onto,
        // and that is an answer rather than a failure - anything else is a failure, and
        // the dialog says so instead of opening on an empty box.
        if (cause instanceof ApiError && (cause.status === 403 || cause.status === 404)) return 'out of reach'

        throw cause
      }
    },
    [quotation.customer.id],
  )

  const customer =
    picked ?? (own.data === 'offered' ? { value: quotation.customer.id, label: quotation.customer.name } : null)

  const lines = quotation.sections.reduce((count, section) => count + section.lines.length, 0)

  async function clone() {
    setSaving(true)
    try {
      const created = await api.post<{ id: string }>(`/quotations/${quotation.id}/clone`, {
        customerId: customer?.value,
        title: title.trim() || null,
      })

      toast.show('Copy created as a draft', 'success')
      onCloned(created.id)
    } catch (cause) {
      toast.show(
        cause instanceof ApiError ? (cause.detail ?? cause.title) : 'Could not clone this quotation',
        'error',
      )
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog
      open
      title="Clone this quotation"
      description="A new draft carrying the same blocks, lines and payment schedule."
      onClose={onClose}
      loading={own.loading}
      error={own.error}
      onRetry={own.reload}
      footer={
        <>
          <Button size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button size="sm" variant="primary" loading={saving} disabled={!customer} onClick={() => void clone()}>
            Create draft
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field
          label="For which customer"
          required
          hint={`Customers in ${quotation.country.name}. The prices on this quotation are in ${quotation.country.currencyCode}.`}
        >
          {(id) => (
            // Same country only: every line carries this quotation's currency, so a copy
            // addressed to another country would put its figures on the wrong letterhead.
            // The API refuses it too.
            <CustomerPicker
              id={id}
              countryId={quotation.country.id}
              countryName={quotation.country.name}
              value={customer}
              onChange={setPicked}
              current={quotation.customer.id}
              placeholder={
                own.data === 'inactive'
                  ? `${quotation.customer.name} is not active - choose another customer`
                  : 'Choose a customer'
              }
            />
          )}
        </Field>

        <Field label="Title" hint="The original's title when left as it is.">
          {(id) => <Input id={id} value={title} onChange={(event) => setTitle(event.target.value)} />}
        </Field>

        <p className="rounded-md bg-sunken px-3 py-2 text-[12.5px] text-muted">
          Copies {quotation.sections.length} block{quotation.sections.length === 1 ? '' : 's'} and{' '}
          {lines} line{lines === 1 ? '' : 's'} at the prices quoted here, and the payment
          schedule. The copy is a draft: it has no number, no approval and no terms frozen
          onto it until it is submitted.
        </p>
      </div>
    </Dialog>
  )
}

function MilestoneRow({
  milestone,
  index,
  count,
  symbol,
  places,
  editable,
  busy,
  onAct,
  dragging,
  over,
  onDragStart,
  onDragOver,
  onDragEnd,
  onDrop,
  onNudge,
}: {
  milestone: MilestoneSummary
  index: number
  count: number
  symbol: string
  places: number
  editable: boolean
  busy: boolean
  onAct: (run: () => Promise<unknown>, success: string) => Promise<void>
  dragging: boolean
  over: boolean
  onDragStart: () => void
  onDragOver: () => void
  onDragEnd: () => void
  onDrop: () => void
  onNudge: (delta: number) => void
}) {
  // The row is only draggable while the handle is held. A row that is draggable at all
  // times cannot have its text selected, and every cell in this one is an input.
  const [armed, setArmed] = useState(false)

  const patch = (body: Record<string, unknown>) =>
    void onAct(() => api.patch(`/quotation-milestones/${milestone.id}`, body), 'Milestone saved')

  if (!editable) {
    return (
      <tr>
        <Td className="relative w-9 pr-0">
          <Rail first={index === 0} last={index === count - 1} />
          <span className="relative z-10 grid size-5 place-items-center rounded-full bg-accent font-mono text-[10px] font-medium text-white">
            {index + 1}
          </span>
        </Td>
        <Td>
          <span className="font-medium">{milestone.name}</span>
          <span className="block text-[12px] text-muted">
            {milestone.condition ?? <span className="text-warn">No condition set</span>}
          </span>
        </Td>
        <Td className="text-muted">{shareLabel(milestone)}</Td>
        <Td numeric>{money(milestone.amount, symbol, places)}</Td>
      </tr>
    )
  }

  return (
    <tr
      draggable={armed}
      onDragStart={(event) => {
        // Firefox starts no drag without data on the transfer.
        event.dataTransfer.setData('text/plain', milestone.id)
        event.dataTransfer.effectAllowed = 'move'
        onDragStart()
      }}
      onDragOver={(event) => {
        event.preventDefault()
        event.dataTransfer.dropEffect = 'move'
        onDragOver()
      }}
      onDrop={(event) => {
        event.preventDefault()
        setArmed(false)
        onDrop()
      }}
      onDragEnd={() => {
        setArmed(false)
        onDragEnd()
      }}
      className={cx(
        dragging && 'opacity-40',
        // Drawn on the row being passed over rather than between rows: the schedule is
        // short, and a line between two table rows is a pixel nobody sees.
        over && 'bg-accent-wash/60',
      )}
    >
      <Td className="relative w-9 pr-0 align-top">
        {/* The stop is the handle. A grip beside a number would be two controls for one
            job, and the number is the thing being moved: grab 3, drop it above 2, and it
            is 2 - the position is read off the rail rather than worked out from it.

            A real button, not a decorative dot. Dragging is the fast path; the arrow keys
            are the one that works with a keyboard, a trackpad nobody can drag with, or a
            screen reader. */}
        <Rail first={index === 0} last={index === count - 1} />

        <button
          type="button"
          disabled={busy}
          aria-label={`Move ${milestone.name}. Payment ${index + 1} of ${count}.`}
          title="Drag to reorder, or focus and use the arrow keys"
          onPointerDown={() => setArmed(true)}
          onPointerUp={() => setArmed(false)}
          onKeyDown={(event) => {
            if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return
            event.preventDefault()
            onNudge(event.key === 'ArrowUp' ? -1 : 1)
          }}
          className="relative z-10 mt-1 grid size-5 cursor-grab select-none place-items-center rounded-full bg-accent font-mono text-[10px] font-medium text-white transition-opacity hover:opacity-85 focus-visible:opacity-85 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {index + 1}
        </button>
      </Td>

      <Td>
        <Input
          aria-label="Milestone name"
          defaultValue={milestone.name}
          className="h-8 w-full min-w-44 py-0"
          onBlur={(event) => event.target.value !== milestone.name && patch({ name: event.target.value })}
        />

        {/* The condition is the point of the row: a figure with no trigger cannot tell
            anybody when it becomes payable, and the submit gate refuses it. */}
        <Input
          aria-label="Condition"
          placeholder="What releases this payment"
          defaultValue={milestone.condition ?? ''}
          className={cx(
            'mt-1.5 h-8 w-full min-w-64 py-0 text-[12.5px]',
            !milestone.condition && 'border-warn/50',
          )}
          onBlur={(event) =>
            event.target.value !== (milestone.condition ?? '') && patch({ condition: event.target.value })
          }
        />
      </Td>

      <Td>
        <span className="flex items-center gap-1.5">
          {/* Basis and mode move together: a percentage of the one-time fee says nothing
              about a subscription, and months say nothing about a fee charged once. One
              control makes the impossible pairings unpickable rather than refused. */}
          <Select
            aria-label="Share in"
            value={`${milestone.basis}:${milestone.mode}`}
            className="h-8 w-32 min-w-32 py-0 pl-2 text-[12.5px]"
            onChange={(event) => {
              const [basis, mode] = event.target.value.split(':')
              patch({
                basis: basis as PaymentMilestoneBasis,
                mode: mode as PaymentShareMode,
                ...(mode === 'Months' && milestone.months < 1 ? { months: 1 } : {}),
              })
            }}
          >
            <option value="OneTime:Percent">% of one-time</option>
            <option value="OneTime:Amount">{symbol} of one-time</option>
            <option value="SubscriptionAdvance:Months">Months in advance</option>
            <option value="SubscriptionAdvance:Amount">{symbol} in advance</option>
          </Select>

          {milestone.mode === 'Months' ? (
            <Input
              key="months"
              aria-label="Months in advance"
              type="number"
              min="1"
              max="120"
              defaultValue={milestone.months}
              className="h-8 w-20 min-w-20 py-0 text-right tabular"
              onBlur={(event) =>
                Number(event.target.value) !== milestone.months &&
                patch({ months: Number(event.target.value) })
              }
            />
          ) : milestone.mode === 'Amount' ? (
            <MoneyInput
              key="amount"
              aria-label="Payment amount"
              defaultValue={milestone.fixedAmount}
              className="h-8 w-24 min-w-24 py-0"
              onBlur={(event) =>
                Number(event.target.value) !== milestone.fixedAmount &&
                patch({ fixedAmount: Number(event.target.value) })
              }
            />
          ) : (
            <Input
              key="percent"
              aria-label="Share percent"
              type="number"
              step="0.01"
              defaultValue={milestone.percent}
              className="h-8 w-20 min-w-20 py-0 text-right tabular"
              onBlur={(event) =>
                Number(event.target.value) !== milestone.percent &&
                patch({ percent: Number(event.target.value) })
              }
            />
          )}
        </span>
      </Td>

      <Td numeric>{money(milestone.amount, symbol, places)}</Td>

      <Td>
        <Button
          size="sm"
          variant="ghost"
          disabled={busy}
          aria-label={`Remove ${milestone.name}`}
          onClick={() =>
            void onAct(() => api.del(`/quotation-milestones/${milestone.id}`), 'Milestone removed')
          }
        >
          &times;
        </Button>
      </Td>
    </tr>
  )
}

function SubscriptionTerms({
  quotation,
  quotationId,
  editable,
  busy,
  onAct,
}: {
  quotation: QuotationDetail
  quotationId: string
  editable: boolean
  busy: boolean
  onAct: (run: () => Promise<unknown>, success: string) => Promise<void>
}) {
  const schedule = quotation.paymentSchedule

  const patch = (body: Record<string, unknown>) =>
    void onAct(() => api.patch(`/quotations/${quotationId}/payment-terms`, body), 'Payment terms saved')

  if (!editable) {
    return (
      <div className="flex flex-col gap-2 border-t border-line-soft px-5 py-4 text-[13px]">
        <Row
          label="Subscription commences"
          value={schedule.subscriptionCommencement ?? date(schedule.subscriptionCommencesOn)}
        />
      </div>
    )
  }

  return (
    <div className="border-t border-line-soft px-5 py-4">
      <p className="mb-3 font-mono text-[11px] uppercase tracking-[0.12em] text-muted">Subscription</p>

      <FormGrid>
        <Field label="Commences" hint="The wording the document prints, e.g. upon go-live.">
          {(id) => (
            <Input
              id={id}
              disabled={busy}
              placeholder="Upon go-live of the first outlet"
              defaultValue={schedule.subscriptionCommencement ?? ''}
              onBlur={(event) =>
                event.target.value !== (schedule.subscriptionCommencement ?? '') &&
                patch({ subscriptionCommencement: event.target.value })
              }
            />
          )}
        </Field>

        <Field label="Or on a fixed date" hint="Both print when both are agreed.">
          {(id) => (
            <Input
              id={id}
              type="date"
              disabled={busy}
              defaultValue={schedule.subscriptionCommencesOn ?? ''}
              onBlur={(event) =>
                event.target.value !== (schedule.subscriptionCommencesOn ?? '') &&
                patch(
                  event.target.value
                    ? { subscriptionCommencesOn: event.target.value }
                    : { clearCommencesOn: true },
                )
              }
            />
          )}
        </Field>

      </FormGrid>
    </div>
  )
}

/**
 * Who the deal came through, and nothing else.
 * <para>
 * Attribution, and what the commission plan is rated against. QuoteHero
 * holds no rate and computes no figure, so there is deliberately nothing here but a name.
 * </para>
 * Read-only once the quotation leaves draft - who a quotation was attributed to is part of
 * what an approver approved, and an approved quotation is immutable. A correction after that
 * is a revision, which copies the reseller forward.
 */
function ResellerCard({
  quotation,
  quotationId,
  editable,
  busy,
  onAct,
}: {
  quotation: QuotationDetail
  quotationId?: string
  editable: boolean
  busy: boolean
  onAct: (run: () => Promise<unknown>, success: string) => Promise<void>
}) {
  // Only fetched when the field can actually be changed: a read-only card already has the
  // name it needs on the quotation itself.
  const resellers = useAsync<Paged<ResellerListItem>>(
    (signal) =>
      editable
        ? api.get<Paged<ResellerListItem>>(
            '/resellers',
            { countryId: quotation.country.id, pageSize: 200 },
            signal,
          )
        : Promise.resolve({ items: [], page: 1, pageSize: 0, totalCount: 0, totalPages: 0, hasMore: false }),
    [editable, quotation.country.id],
  )

  return (
    <Card>
      <CardHeader title="Reseller" hint="Attribution. No money attached." />
      <div className="flex flex-col gap-3 px-5 py-4 text-[13px]">
        {editable ? (
          <Field label="Who the deal came through">
            {(fieldId) => (
              <Select
                id={fieldId}
                value={quotation.reseller?.id ?? ''}
                disabled={busy || resellers.loading}
                onChange={(event) => {
                  const next = event.target.value

                  void onAct(
                    () =>
                      api.patch(
                        `/quotations/${quotationId}`,
                        // A cleared picklist is an explicit instruction, not an omission:
                        // a null resellerId on its own means "leave it alone".
                        next ? { resellerId: next } : { clearReseller: true },
                      ),
                    next ? 'Reseller saved' : 'Reseller removed',
                  )
                }}
              >
                <option value="">Direct - no reseller</option>
                {resellers.data?.items.map((reseller) => (
                  <option key={reseller.id} value={reseller.id}>
                    {reseller.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        ) : (
          <Row label="Attributed to" value={quotation.reseller?.name ?? 'Direct - no reseller'} />
        )}

        <p className="text-[12px] text-muted">
          Commission is earned on the payments this customer makes, at the rates frozen onto
          this quotation when it was submitted.
        </p>
      </div>
    </Card>
  )
}

function TotalsPanel({ quotation }: { quotation: QuotationDetail }) {
  const { totals, country } = quotation

  // The symbol the document will print, not the ISO code. A rep should never have to
  // reconcile "MYR 100,029.60" on screen with "RM 100,029.60" on the page they send.
  const symbol = country.currencySymbol
  const places = country.currencyDecimals

  return (
    <Card>
      <CardHeader title="Summary" hint="Derived from the lines. Nothing here can be typed." />
      <div className="flex flex-col gap-3 px-5 py-4 text-[13px]">
        <Row label="One-time" value={money(totals.oneTimeTotal, symbol, places)} />
        {/* What the monthly lines bill each month - the figure the customer sees on every
            invoice. Already inside year one, twelve times over, so it is not added again. */}
        <Row label="Recurring, monthly" value={money(totals.recurringMonthlyNet, symbol, places)} />
        <Row label="Recurring, year one" value={money(totals.recurringYearOneTotal, symbol, places)} />
        {totals.discountAmount > 0 && (
          <Row label="Discount" value={`- ${money(totals.discountAmount, symbol, places)}`} />
        )}
        <Row label="Subtotal" value={money(totals.subtotal, symbol, places)} />
        <Row
          label={`${totals.taxLabel} ${percent(totals.taxRate, 2)}`}
          value={money(totals.taxAmount, symbol, places)}
        />

        <div className="mt-1 flex items-baseline justify-between gap-3 border-t border-line pt-3">
          <span className="font-medium">Total, year one</span>
          <span className="tabular text-base font-semibold">
            {money(totals.grandTotal, symbol, places)}
          </span>
        </div>

        {totals.recurringAnnualGross > 0 && (
          <p className="text-[12px] text-muted">
            Then {money(totals.recurringAnnualGross, symbol, places)} a year, tax included. A
            first-year figure alone hides the ongoing commitment.
          </p>
        )}
      </div>
    </Card>
  )
}

function SectionCard({
  section,
  quotation,
  editable,
  isFirst,
  isLast,
  busy,
  onAddLine,
  onAct,
  sectionIds,
  quotationId,
}: {
  section: SectionSummary
  quotation: QuotationDetail
  editable: boolean
  isFirst: boolean
  isLast: boolean
  busy: boolean
  onAddLine: () => void
  onAct: (run: () => Promise<unknown>, success: string) => Promise<void>
  sectionIds: string[]
  quotationId: string
}) {
  const toast = useToast()
  const [copying, setCopying] = useState(false)

  const symbol = quotation.country.currencySymbol
  const places = quotation.country.currencyDecimals

  /**
   * Puts the catalogue's paragraph on this block, when the block was added before there
   * was one to copy.
   *
   * A block takes the product's narrative at insert and then keeps it, because a quotation
   * is a snapshot - editing the catalogue must never rewrite what a customer was sent. That
   * leaves one gap worth a button: a product written up after the block was added has a
   * paragraph the rep can read in the catalogue and no way to get onto the quotation.
   * Asked for, never automatic, so the snapshot is still the rep's own doing.
   */
  async function takeFromProduct() {
    if (!section.productId) return

    setCopying(true)
    try {
      const product = await api.get<ProductDetail>(`/products/${section.productId}`)

      if (isRichTextEmpty(product.narrativeHtml)) {
        toast.show(`${product.name} has no narrative yet. Write it in the catalogue first.`, 'error')
        return
      }

      await onAct(
        () =>
          api.patch(`/quotation-sections/${section.id}`, { narrativeHtml: product.narrativeHtml }),
        'Remarks taken from the product',
      )
    } catch (cause) {
      toast.show(cause instanceof ApiError ? cause.message : 'Could not read that product', 'error')
    } finally {
      setCopying(false)
    }
  }

  function move(direction: -1 | 1) {
    const order = [...sectionIds]
    const from = order.indexOf(section.id)
    const to = from + direction
    const moved = order[from]
    const displaced = order[to]
    if (moved === undefined || displaced === undefined) return

    order[from] = displaced
    order[to] = moved

    void onAct(
      () => api.put(`/quotations/${quotationId}/sections/order`, { sectionIdsInOrder: order }),
      'Blocks reordered',
    )
  }

  return (
    <Card>
      <CardHeader
        title={section.title}
        hint={section.summaryLine ?? undefined}
        action={
          editable && (
            <div className="flex items-center gap-1">
              <Button size="sm" variant="ghost" disabled={isFirst || busy} onClick={() => move(-1)} aria-label="Move block up">
                ↑
              </Button>
              <Button size="sm" variant="ghost" disabled={isLast || busy} onClick={() => move(1)} aria-label="Move block down">
                ↓
              </Button>
              <Button size="sm" onClick={onAddLine}>
                Add line
              </Button>
              <Button
                size="sm"
                variant="danger"
                onClick={() =>
                  void onAct(() => api.del(`/quotation-sections/${section.id}`), 'Block removed')
                }
              >
                Remove
              </Button>
            </div>
          )
        }
      />

      {section.modules.length > 0 && (
        // Padded below as well as above. Without it the last row of module pills sat
        // directly on whatever follows - the remarks box, or the LINE / BILLING header when
        // a read-only block has nothing remarked on it - and two unrelated things touching
        // read as one thing.
        <ul className="flex flex-wrap gap-1.5 px-5 pb-4 pt-4">
          {section.modules.map((module) => (
            <li key={module} className="rounded-sm bg-sunken px-2 py-0.5 text-[12px] text-ink-2">
              {module}
            </li>
          ))}
        </ul>
      )}

      {/* Remarks: the block's own paragraph, copied from the product and edited here. It
          belongs between the modules and the ladder because that is the order the block
          reads in - what it is, what is in it, then what it costs. Empty and read-only, it
          is not rendered at all rather than shown as a disabled empty box. */}
      {(editable || !isRichTextEmpty(section.narrativeHtml)) && (
        <div className={cx('px-5 pb-4', section.modules.length === 0 && 'pt-4')}>
          <Field
            label="Remarks"
            hint="Headings, lists, colour and links print as they look here, in smaller type than the introduction."
          >
            {(fieldId) => {
              const stored = asRichHtml(section.narrativeHtml)

              return (
                <>
                  <RichTextEditor
                    id={fieldId}
                    value={section.narrativeHtml}
                    disabled={!editable}
                    placeholder="What this block means for this customer."
                    onBlur={(html) =>
                      html !== stored &&
                      void onAct(
                        () =>
                          api.patch(`/quotation-sections/${section.id}`, { narrativeHtml: html }),
                        'Remarks saved',
                      )
                    }
                  />

                  {/* Only with nothing written: a block whose remarks somebody has already
                      typed is not offered a button that would replace them. */}
                  {editable && section.productId && isRichTextEmpty(section.narrativeHtml) && (
                    <div>
                      <Button
                        size="sm"
                        variant="ghost"
                        loading={copying}
                        onClick={() => void takeFromProduct()}
                      >
                        Use the product's narrative
                      </Button>
                    </div>
                  )}
                </>
              )
            }}
          </Field>
        </div>
      )}

      {section.lines.length === 0 ? (
        <div className="px-5 py-6 text-[13px] text-muted">
          No priced lines yet. A block with no lines contributes nothing to the total.
        </div>
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>Line</Th>
              <Th>Billing</Th>
              <Th numeric>Qty</Th>
              <Th numeric className="whitespace-nowrap">Unit price</Th>
              <Th numeric>Disc %</Th>
              <Th numeric>Total</Th>
              {editable && <Th />}
            </tr>
          </thead>
          <tbody>
            {section.lines.map((line) => (
              <LineRow
                key={line.id}
                line={line}
                editable={editable}
                symbol={symbol}
                places={places}
                threshold={quotation.country.discountApprovalThresholdPercent}
                onAct={onAct}
              />
            ))}
          </tbody>
        </Table>
      )}

      <div className="flex items-center justify-end gap-3 border-t border-line-soft px-5 py-3 text-[13px]">
        <span className="text-muted">Block total</span>
        <span className="tabular font-medium">{money(section.sectionTotal, symbol, places)}</span>
      </div>
    </Card>
  )
}

/**
 * Approve, or send it back with a reason.
 *
 * A rejection carries a required comment and an approval does not: "changes needed" with
 * no statement of what needs changing is a round trip that teaches the preparer nothing.
 */
function DecideDialog({
  requestId,
  approve,
  isOverride,
  summary,
  onClose,
  onDone,
}: {
  requestId: string
  approve: boolean
  isOverride: boolean
  summary: string
  onClose: () => void
  onDone: () => void
}) {
  const toast = useToast()
  const [comment, setComment] = useState('')
  const [saving, setSaving] = useState(false)

  async function decide() {
    setSaving(true)
    try {
      const result = await api.post<{ nextStepLabel: string | null }>(
        `/approvals/${requestId}/${approve ? 'approve' : 'reject'}`,
        { comment: comment || null },
      )

      toast.show(
        approve
          ? result.nextStepLabel
            ? `Approved. Now with ${result.nextStepLabel}.`
            : 'Approved. The chain is complete.'
          : 'Sent back to the preparer with your reason.',
        'success',
      )

      onDone()
    } catch (cause) {
      toast.show(cause instanceof ApiError ? cause.message : 'Could not record the decision', 'error')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog
      open
      title={approve ? 'Approve this quotation' : 'Send it back'}
      description={summary}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant={approve ? 'primary' : 'danger'}
            loading={saving}
            disabled={!approve && comment.trim().length === 0}
            onClick={() => void decide()}
          >
            {approve ? 'Approve' : 'Send back'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {isOverride && (
          <p className="rounded-md bg-warn-wash px-3 py-2 text-[13px] text-warn">
            This step names somebody else. You can act on it, and it will be recorded as an
            override against your name - it does not skip anybody.
          </p>
        )}

        <Field
          label={approve ? 'Comment' : 'What needs changing'}
          required={!approve}
          hint={
            approve
              ? 'Optional. It is kept against the approval either way.'
              : 'The preparer sees this. Say what to fix, not that something is wrong.'
          }
        >
          {(id) => (
            <Textarea
              id={id}
              rows={4}
              value={comment}
              onChange={(event) => setComment(event.target.value)}
              placeholder={approve ? 'Anything worth recording' : 'The discount on line 2 needs...'}
            />
          )}
        </Field>
      </div>
    </Dialog>
  )
}

function LineRow({
  line,
  editable,
  symbol,
  places,
  threshold,
  onAct,
}: {
  line: LineSummary
  editable: boolean
  symbol: string
  places: number
  threshold: number
  onAct: (run: () => Promise<unknown>, success: string) => Promise<void>
}) {
  function patch(body: Record<string, unknown>) {
    void onAct(() => api.patch(`/quotation-lines/${line.id}`, body), 'Line saved')
  }

  return (
    <tr>
      <Td>
        {editable ? (
          <Input
            defaultValue={line.description}
            className="h-8 w-full min-w-72 py-0"
            onBlur={(event) =>
              event.target.value !== line.description && patch({ description: event.target.value })
            }
          />
        ) : (
          line.description
        )}
      </Td>
      <Td>
        <Badge tone={line.billingType === 'OneTime' ? 'neutral' : 'accent'}>
          {line.billingType === 'OneTime' ? 'One-time' : line.billingType}
        </Badge>
        <span className="ml-1.5 text-[12px] text-muted">{line.uom}</span>
      </Td>
      <Td numeric>
        {editable ? (
          <Input
            type="number"
            step="0.01"
            defaultValue={line.quantity}
            className="h-8 w-20 min-w-20 py-0 text-right tabular"
            onBlur={(event) =>
              Number(event.target.value) !== line.quantity && patch({ quantity: Number(event.target.value) })
            }
          />
        ) : (
          line.quantity
        )}
      </Td>
      <Td numeric>
        {editable ? (
          <MoneyInput
            defaultValue={line.unitPrice}
            className="h-8 w-28 min-w-28 py-0"
            onBlur={(event) =>
              Number(event.target.value) !== line.unitPrice && patch({ unitPrice: Number(event.target.value) })
            }
          />
        ) : (
          money(line.unitPrice, symbol, places)
        )}
      </Td>
      <Td numeric>
        <span className="flex items-center justify-end gap-1.5">
          {editable ? (
            <>
              {/* Which unit the figure beside it is in. Switching sends the mode on its
                  own, so the figure already typed is kept and simply reinterpreted. */}
              <Select
                aria-label="Discount in"
                value={line.discountMode}
                className="h-8 w-16 min-w-16 py-0 pl-2 text-[12.5px]"
                onChange={(event) => patch({ discountMode: event.target.value as DiscountMode })}
              >
                <option value="Percent">%</option>
                <option value="Amount">{symbol}</option>
              </Select>

              {line.discountMode === 'Amount' ? (
                <MoneyInput
                  key="amount"
                  aria-label="Discount amount"
                  defaultValue={line.fixedDiscountAmount}
                  className="h-8 w-24 min-w-24 py-0"
                  onBlur={(event) =>
                    Number(event.target.value) !== line.fixedDiscountAmount &&
                    patch({ fixedDiscountAmount: Number(event.target.value) })
                  }
                />
              ) : (
                <Input
                  key="percent"
                  aria-label="Discount percent"
                  type="number"
                  step="0.01"
                  defaultValue={line.discountPercent}
                  className="h-8 w-20 min-w-20 py-0 text-right tabular"
                  onBlur={(event) =>
                    Number(event.target.value) !== line.discountPercent &&
                    patch({ discountPercent: Number(event.target.value) })
                  }
                />
              )}
            </>
          ) : line.discountMode === 'Amount' ? (
            line.fixedDiscountAmount > 0 ? money(line.fixedDiscountAmount, symbol, places) : '-'
          ) : (
            percent(line.discountPercent, 2)
          )}

          {/* Not a warning: a discount over the threshold is allowed, it simply pulls
              another approver into the chain. Saying so here beats a surprise at submit.
              Measured on the effective percentage, so a sum off counts the same as the
              percentage it works out to. */}
          {line.exceedsDiscountThreshold && (
            <span title={`${percent(line.effectiveDiscountPercent, 2)} is above the ${percent(threshold, 0)} country threshold, so this adds an approver`}>
              <Badge tone="warning">+1</Badge>
            </span>
          )}
        </span>
      </Td>
      <Td numeric>{money(line.lineTotal, symbol, places)}</Td>
      {editable && (
        <Td>
          <Button
            size="sm"
            variant="ghost"
            aria-label={`Remove ${line.description}`}
            onClick={() => void onAct(() => api.del(`/quotation-lines/${line.id}`), 'Line removed')}
          >
            ✕
          </Button>
        </Td>
      )}
    </tr>
  )
}

function AddSection({
  open,
  quotationId,
  onClose,
  onAdded,
}: {
  open: boolean
  quotationId: string
  onClose: () => void
  onAdded: () => void
}) {
  const toast = useToast()
  const [productId, setProductId] = useState('')
  const [includeAll, setIncludeAll] = useState(true)
  const [saving, setSaving] = useState(false)

  const products = useAsync<Paged<ProductListItem>>(
    (signal) =>
      open
        ? // By name, said here as well as defaulted there: this is a list somebody reads
          // down looking for a product they already have in mind.
          api.get<Paged<ProductListItem>>('/products', { pageSize: 200, sort: 'name' }, signal)
        : Promise.resolve({ items: [], page: 1, pageSize: 0, totalCount: 0, totalPages: 0, hasMore: false }),
    [open],
  )

  async function add() {
    setSaving(true)
    try {
      // No title: the product's name, or "New solution" for an empty block. Naming it
      // here was a second place to type something the block itself already edits, and
      // whatever was typed had to be retyped to correct it.
      await api.post(`/quotations/${quotationId}/sections`, {
        productId: productId || null,
        includeAllPriceItems: includeAll,
      })

      onAdded()
      setProductId('')
    } catch (cause) {
      toast.show(cause instanceof ApiError ? cause.message : 'Could not add the block', 'error')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog
      open={open}
      title="Add solution block"
      description="Seeded from the catalogue, then editable here - the block's own title included. The quotation keeps its own copy, so renaming a product later never rewrites what was quoted."
      onClose={onClose}
      loading={products.loading}
      error={products.error}
      onRetry={products.reload}
      footer={
        <>
          <Button size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button size="sm" variant="primary" loading={saving} onClick={() => void add()}>
            Add block
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label="Product" hint="Brings its narrative, module bullets and pricing ladder with it.">
          {(id) => (
            <Select id={id} value={productId} onChange={(event) => setProductId(event.target.value)}>
              <option value="">No product - an empty block</option>
              {products.data?.items.map((product) => (
                <option key={product.id} value={product.id} disabled={!product.readyToQuote}>
                  {product.name}
                  {product.readyToQuote ? '' : ' — not priced in every country'}
                </option>
              ))}
            </Select>
          )}
        </Field>

        <Checkbox
          label="Bring every price item in"
          checked={includeAll}
          onChange={(event) => setIncludeAll(event.target.checked)}
        />
      </div>
    </Dialog>
  )
}

function AddLine({
  section,
  quotation,
  onClose,
  onAdded,
}: {
  section: SectionSummary | undefined
  quotation: QuotationDetail
  onClose: () => void
  onAdded: () => void
}) {
  const toast = useToast()
  const [priceItemId, setPriceItemId] = useState('')
  const [description, setDescription] = useState('')
  const [quantity, setQuantity] = useState('1')
  const [discountMode, setDiscountMode] = useState<DiscountMode>('Percent')
  const [discount, setDiscount] = useState('0')
  const [saving, setSaving] = useState(false)

  const product = useAsync<ProductDetail | undefined>(
    (signal) =>
      section?.productId
        ? api.get<ProductDetail>(`/products/${section.productId}`, undefined, signal)
        : Promise.resolve(undefined),
    [section?.productId],
  )

  async function add() {
    if (!section) return

    setSaving(true)
    try {
      await api.post(`/quotation-sections/${section.id}/lines`, {
        productPriceItemId: priceItemId || null,
        description: description || null,
        quantity: Number(quantity),
        discountMode,
        discountPercent: discountMode === 'Percent' ? Number(discount) : 0,
        fixedDiscountAmount: discountMode === 'Amount' ? Number(discount) : 0,
      })

      onAdded()
      setPriceItemId('')
      setDescription('')
      setQuantity('1')
      setDiscountMode('Percent')
      setDiscount('0')
    } catch (cause) {
      toast.show(cause instanceof ApiError ? cause.message : 'Could not add the line', 'error')
    } finally {
      setSaving(false)
    }
  }

  // A sum off is measured as the percentage it works out to, the same way the API measures
  // it, so the warning appears for exactly the lines that will pull in another approver.
  const unitPrice = product.data?.priceItems
    .find((item) => item.id === priceItemId)
    ?.prices.find((price) => price.countryId === quotation.country.id)?.unitPrice

  const gross = (unitPrice ?? 0) * Number(quantity || 0)

  const effectiveDiscount =
    discountMode === 'Percent'
      ? Number(discount || 0)
      : gross > 0
        ? (Number(discount || 0) / gross) * 100
        : 0

  const overThreshold = effectiveDiscount > quotation.country.discountApprovalThresholdPercent

  const overTheLine = discountMode === 'Amount' && gross > 0 && Number(discount || 0) > gross

  return (
    <Dialog
      open={section !== undefined}
      title="Add line"
      description="Priced from the catalogue at today's effective price, then copied onto the quotation."
      onClose={onClose}
      loading={product.loading}
      error={product.error}
      onRetry={product.reload}
      footer={
        <>
          <Button size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            variant="primary"
            loading={saving}
            disabled={!priceItemId && !description}
            onClick={() => void add()}
          >
            Add line
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {product.data && (
          <Field label="Catalogue item">
            {(id) => (
              <Select id={id} value={priceItemId} onChange={(event) => setPriceItemId(event.target.value)}>
                <option value="">A line of my own</option>
                {product.data?.priceItems.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name} · {item.billingType === 'OneTime' ? 'one-time' : item.billingType} · per{' '}
                    {item.uom}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        )}

        {!priceItemId && (
          <Field label="Description" required hint="A line with no catalogue item needs its own description.">
            {(id) => (
              <Input id={id} value={description} onChange={(event) => setDescription(event.target.value)} />
            )}
          </Field>
        )}

        <Field label="Quantity">
          {(id) => (
            <Input
              id={id}
              type="number"
              step="0.01"
              value={quantity}
              onChange={(event) => setQuantity(event.target.value)}
            />
          )}
        </Field>

        <Field
          label="Discount"
          error={
            overTheLine
              ? `This line is worth ${money(gross, quotation.country.currencySymbol, quotation.country.currencyDecimals)}. A discount cannot be larger than the line.`
              : undefined
          }
          hint={
            overThreshold
              ? `${percent(effectiveDiscount, 2)} is above the ${percent(quotation.country.discountApprovalThresholdPercent, 0)} threshold for ${quotation.country.isoCode}, so this adds an approver.`
              : 'A percentage follows the quantity. A sum comes off the line once, whatever the quantity.'
          }
        >
          {(id) => (
            <div className="flex gap-2">
              <Select
                aria-label="Discount in"
                value={discountMode}
                className="w-28"
                onChange={(event) => setDiscountMode(event.target.value as DiscountMode)}
              >
                <option value="Percent">Percent %</option>
                <option value="Amount">{quotation.country.currencySymbol} off</option>
              </Select>

              <Input
                id={id}
                type="number"
                step="0.01"
                value={discount}
                onChange={(event) => setDiscount(event.target.value)}
              />
            </div>
          )}
        </Field>
      </div>
    </Dialog>
  )
}

/** The two answers the header offers. A decline is a kind of loss, and is told apart in its dialog. */
export type OutcomeChoice = 'Won' | 'Lost'

/**
 * What the Record outcome button opens onto.
 *
 * <p>
 * Always both, so the control is the same shape in every state it appears in. Once the
 * customer has accepted, a loss is not something that can be recorded - it would contradict
 * a signature they gave, and the API refuses it - so that entry is still there, cannot be
 * pressed, and says where to go instead. Left out, the menu would be one item long and
 * somebody looking for the other would have nothing telling them why it had gone.
 * </p>
 *
 * Exported so which entries are live can be asserted without rendering the page.
 */
export function outcomeChoices(
  status: QuotationStatus,
  choose: (outcome: OutcomeChoice) => void,
): ActionMenuItem[] {
  const lossOffered = canRecordLoss(status)

  return [
    { label: 'Mark as Won', onSelect: () => choose('Won') },
    {
      label: 'Mark as Lost',
      onSelect: () => choose('Lost'),
      disabled: !lossOffered,
      ...(lossOffered ? {} : { hint: 'The customer has accepted. If the deal is off, cancel the quotation.' }),
    },
  ]
}

/**
 * Closes a quotation out, one way or the other.
 *
 * <p>
 * The header has already asked which, so this only confirms it. Still a dialog and not an
 * immediate act: every revenue figure in the product is downstream of this, and neither
 * answer can be taken back from here.
 * </p>
 *
 * Mounted only while it is open, so a reason typed for one quotation's loss is never still
 * in the box the next time it opens.
 */
export function RecordOutcome({
  outcome,
  quotationId,
  onClose,
  onRecorded,
}: {
  outcome: OutcomeChoice
  quotationId: string
  onClose: () => void
  onRecorded: () => void
}) {
  const toast = useToast()
  const [kind, setKind] = useState<'Lost' | 'Declined'>('Lost')
  const [reason, setReason] = useState('')
  const [saving, setSaving] = useState(false)

  const won = outcome === 'Won'
  const recorded = won ? 'Won' : kind

  async function record() {
    setSaving(true)
    try {
      await api.post(`/quotations/${quotationId}/outcome`, {
        outcome: recorded,
        reason: won ? null : reason,
      })
      toast.show(`Recorded as ${recorded.toLowerCase()}`, 'success')
      onRecorded()
    } catch (cause) {
      toast.show(cause instanceof ApiError ? cause.message : 'Could not record the outcome', 'error')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog
      open
      title={won ? 'Mark as won' : 'Mark as lost'}
      description="Every figure on the dashboard is downstream of this. A pipeline nobody closes reports a rising forecast and a win rate of zero."
      onClose={onClose}
      footer={
        <>
          <Button size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            variant="primary"
            loading={saving}
            disabled={!won && reason.trim().length === 0}
            onClick={() => void record()}
          >
            {won ? 'Mark as won' : 'Mark as lost'}
          </Button>
        </>
      }
    >
      {won ? (
        // Said before the button is pressed rather than left to the API's refusal: a win
        // has to have a document behind it, and this is the moment somebody finds out
        // whether theirs does.
        <p className="rounded-md bg-sunken px-3 py-2 text-[12.5px] text-muted">
          A win needs the signed quotation behind it: either the customer signed online
          through their link, or the copy they chopped and signed has been uploaded here.
          Without one of those this is refused.
        </p>
      ) : (
        <div className="flex flex-col gap-4">
          {/* A customer who said no and a deal that went elsewhere are both lost, and the
              figures count them the same. Kept apart because the next quotation learns
              different things from each. */}
          <Field label="Outcome">
            {(id) => (
              <Select id={id} value={kind} onChange={(event) => setKind(event.target.value as 'Lost' | 'Declined')}>
                <option value="Lost">Lost</option>
                <option value="Declined">Declined by the customer</option>
              </Select>
            )}
          </Field>

          <Field
            label="What happened"
            required
            hint="Mandatory on a loss. Without it the next quotation learns nothing from this one."
          >
            {(id) => (
              <Textarea id={id} value={reason} onChange={(event) => setReason(event.target.value)} />
            )}
          </Field>
        </div>
      )}
    </Dialog>
  )
}

function DocumentPreview({
  open,
  quotation,
  onClose,
}: {
  open: boolean
  quotation: QuotationDetail
  onClose: () => void
}) {
  const toast = useToast()
  const [url, setUrl] = useState<string | undefined>()
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!open) return

    let live = true
    let objectUrl: string | undefined

    setLoading(true)

    // Fetched rather than linked: the PDF endpoint needs the access token, and an
    // <iframe src> carries no Authorization header.
    requestBlob(`/quotations/${quotation.id}/pdf`, { query: { download: false } })
      .then((blob) => {
        if (!live) return
        objectUrl = URL.createObjectURL(blob)
        setUrl(objectUrl)
      })
      .catch((cause: unknown) => {
        if (!live) return
        toast.show(cause instanceof ApiError ? cause.message : 'Could not build the document', 'error')
      })
      .finally(() => {
        if (live) setLoading(false)
      })

    return () => {
      live = false
      // Revoked on close: an object URL held for the life of the tab is a whole PDF
      // the browser cannot reclaim.
      if (objectUrl) URL.revokeObjectURL(objectUrl)
      setUrl(undefined)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, quotation.id])

  async function download() {
    try {
      const blob = await requestBlob(`/quotations/${quotation.id}/pdf`, { query: { download: true } })
      const href = URL.createObjectURL(blob)
      const anchor = document.createElement('a')

      anchor.href = href
      anchor.download = `${quotation.quotationNo ?? 'quotation'}.pdf`
      anchor.click()

      URL.revokeObjectURL(href)
    } catch (cause) {
      toast.show(cause instanceof ApiError ? cause.message : 'Could not build the document', 'error')
    }
  }

  return (
    <Dialog
      open={open}
      title="Quotation document"
      description="Built now from the record, merged with the frozen terms, and never stored. Anything short of approved renders watermarked."
      onClose={onClose}
      document
      footer={
        <>
          <Button size="sm" onClick={onClose}>
            Close
          </Button>
          <Button size="sm" variant="primary" onClick={() => void download()}>
            Download PDF
          </Button>
        </>
      }
    >
      {loading && <p className="py-8 text-center text-[13px] text-muted">Building the document…</p>}
      {url && (
        <iframe
          src={url}
          title="Quotation document preview"
          className="h-[74vh] w-full rounded-md border border-line"
        />
      )}
    </Dialog>
  )
}
